import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
    Instagram,
    Phone,
    Mail,
    MapPin,
    Clock,
    ShieldCheck,
    Sparkles,
    Microscope,
    Stethoscope,
    GraduationCap,
    Award,
    Heart,
    Languages,
    ArrowRight,
    Quote,
    Menu,
    X,
    Sun,
    Droplets,
    Zap,
    FlaskConical,
    Baby,
    Wind,
    Activity,
    Bone,
    Syringe,
    Dumbbell,
    HeartPulse,
    Bandage,
    Video,
    Aperture,
    PersonStanding,
    Hand,
} from "lucide-react";

/* WhatsApp brand glyph (lucide does not ship a WhatsApp logo). */
const WhatsappIcon = ({ className = "w-4 h-4" }) => (
    <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="currentColor"
    >
        <path d="M19.11 4.91A10.46 10.46 0 0 0 11.64.5C5.94.5 1.3 5.14 1.3 10.85a10.3 10.3 0 0 0 1.4 5.18L1.2 21.5l5.6-1.47a10.36 10.36 0 0 0 4.84 1.22h.01c5.7 0 10.34-4.64 10.34-10.35a10.3 10.3 0 0 0-3.08-7.34zM11.65 19.5h-.01a8.62 8.62 0 0 1-4.4-1.2l-.32-.19-3.32.87.89-3.24-.21-.33a8.6 8.6 0 0 1-1.32-4.57c0-4.76 3.87-8.63 8.63-8.63 2.31 0 4.47.9 6.1 2.53a8.59 8.59 0 0 1 2.53 6.1c0 4.76-3.87 8.63-8.63 8.63zm4.96-6.46c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.18-1.35-.81-.72-1.35-1.61-1.5-1.88-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.83-2.01-.22-.53-.45-.46-.61-.46l-.52-.01a1 1 0 0 0-.72.34c-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.11 2.81.14.18 1.91 2.92 4.62 4.09.65.28 1.15.45 1.55.58.65.21 1.24.18 1.71.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.17.16-1.28-.07-.11-.25-.18-.52-.32z" />
    </svg>
);

/* Knee / joint glyph (lucide has no anatomical knee icon). */
const KneeIcon = ({ className = "w-4 h-4" }) => (
    <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M9 2v6.5a3.5 3.5 0 0 0 3.5 3.5H15" />
        <path d="M15 22v-6.5a3.5 3.5 0 0 1 3.5-3.5H9" />
        <circle cx="6.5" cy="11.5" r="1.6" />
    </svg>
);

/* Hair strands glyph (lucide has no hair icon). */
const HairIcon = ({ className = "w-4 h-4" }) => (
    <svg
        viewBox="0 0 24 24"
        aria-hidden="true"
        className={className}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M6 3c-2 3 2 4 0 7s2 4 0 7" />
        <path d="M12 3c-2 3 2 4 0 7s2 4 0 7" />
        <path d="M18 3c-2 3 2 4 0 7s2 4 0 7" />
    </svg>
);

const LOGO_URL =
    "https://customer-assets.emergentagent.com/job_pranya-skin/artifacts/1r6l89e5_707509538_18096115606918877_8950106289252762897_n.jpg";
const INSTAGRAM_URL = "https://www.instagram.com/pranya.boneskin_clinic/";
const PHONE_NUMBER = "+919491072309";
const PHONE_DISPLAY = "+91 94910 72309";
const WHATSAPP_NUMBER = "919491072309";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hi Pranya Clinic, I would like to book an appointment."
)}`;
const EMAIL = "bookings@pranyaclinic.com";
const ADDRESS_LINE = "Visakhapatnam, Andhra Pradesh 530007";
const MAPS_DIRECTIONS_URL = "https://maps.app.goo.gl/QZLaeAqd5YfYwpj48";
const MAPS_EMBED_URL =
    "https://maps.google.com/maps?q=17.7460372,83.2680866&z=17&output=embed";

const NAV_LINKS = [
    { id: "about", label: "About" },
    { id: "doctors", label: "Doctors" },
    { id: "services", label: "Services" },
    { id: "why-us", label: "Why Us" },
    { id: "faq", label: "FAQ" },
    { id: "contact", label: "Contact" },
];

const SERVICES_DERM = [
    {
        icon: Droplets,
        title: "Acne & Pimples",
        copy: "Medical-grade routines, peels, and procedural care for clear, confident skin.",
    },
    {
        icon: Sun,
        title: "Pigmentation & Melasma",
        copy: "Tailored regimens for tan, dark spots, and uneven tone using dermoscopic assessment.",
    },
    {
        icon: HairIcon,
        title: "Hair Loss & Hair Care",
        copy: "Evidence-based protocols including PRP, mesotherapy, and trichology consults.",
    },
    {
        icon: Sparkles,
        title: "Vitiligo Surgery",
        copy: "Specialised surgical management for stable vitiligo — restoring natural pigment.",
    },
    {
        icon: Microscope,
        title: "Dermoscopy",
        copy: "Advanced diagnostic imaging for accurate skin, hair, and nail evaluation.",
    },
    {
        icon: Zap,
        title: "Laser Treatments",
        copy: "Hair reduction, scar revision, and pigmentation correction with modern lasers.",
    },
    {
        icon: FlaskConical,
        title: "Skin Allergies & Eczema",
        copy: "Diagnosis and personalised care for chronic and acute skin sensitivities.",
    },
    {
        icon: Activity,
        title: "Minor Skin Surgeries",
        copy: "Cysts, moles, warts, biopsies and cryotherapy — performed safely in-clinic.",
    },
    {
        icon: Wind,
        title: "Anti-Ageing & Glow",
        copy: "Preventive skincare, chemical peels, and rejuvenation programmes.",
    },
    {
        icon: Baby,
        title: "Paediatric Dermatology",
        copy: "Gentle, age-appropriate skin care for infants, children, and adolescents.",
    },
];

const SERVICES_ORTHO = [
    {
        icon: Aperture,
        title: "Arthroscopic (Keyhole) Surgery",
        copy: "Minimally invasive procedures for ligament injuries, cartilage damage, and joint repair.",
    },
    {
        icon: KneeIcon,
        title: "Knee, Hip & Elbow Joint Replacement",
        copy: "Modern joint replacement surgeries to restore mobility and reduce chronic pain.",
    },
    {
        icon: PersonStanding,
        title: "Spine Fixation & Trauma",
        copy: "Specialised surgical care for spine injuries, fractures, and stabilisation procedures.",
    },
    {
        icon: Hand,
        title: "Arthritis & Joint Disorders",
        copy: "Medical and surgical management of osteoarthritis, RA, and degenerative joint conditions.",
    },
    {
        icon: Bandage,
        title: "Trauma & Fracture Care",
        copy: "Prompt diagnosis, fixation, and rehab for fractures of all kinds — adults and elderly.",
    },
    {
        icon: Dumbbell,
        title: "Sports Injuries & Rehab",
        copy: "ACL, meniscus, rotator cuff and overuse injuries — assessed, treated, rehabilitated.",
    },
    {
        icon: Syringe,
        title: "PRP Therapy",
        copy: "Platelet-rich plasma injections for joint pain, tendon healing, and recovery support.",
    },
    {
        icon: Droplets,
        title: "Hyaluronic Acid Injections",
        copy: "Targeted knee osteoarthritis relief through viscosupplementation injections.",
    },
    {
        icon: Stethoscope,
        title: "Bone & Musculoskeletal Care",
        copy: "Comprehensive consultations for joint pain, posture, back issues, and mobility.",
    },
];

const WHY_US = [
    {
        icon: GraduationCap,
        title: "Specialist-Led Care",
        copy: "MS Orthopaedics, MD Dermatology and visiting consultants — never junior or general practitioners.",
    },
    {
        icon: Microscope,
        title: "Modern Diagnostics",
        copy: "Dermoscopy, surgical instrumentation and imaging-led decisions across every speciality.",
    },
    {
        icon: Heart,
        title: "Personalised Care",
        copy: "Plans built around your condition, lifestyle, and goals — bone, skin, or gut. Never a template.",
    },
    {
        icon: ShieldCheck,
        title: "Safe & Hygienic",
        copy: "Strict sterilisation, modern OT protocols, and structured aftercare follow-ups for every procedure.",
    },
];

const TESTIMONIALS = [
    {
        name: "Ramesh V.",
        location: "Madhurawada · Knee replacement",
        quote: "I was scared about knee surgery, but Dr. Prashanth explained every step honestly. Two months after the replacement I'm walking pain-free for the first time in years.",
    },
    {
        name: "Priya R.",
        location: "Visakhapatnam · Dermatology",
        quote: "Doctor garu explained my pigmentation issue so clearly. After three months of her plan, my skin looks the calmest it has in years — no upselling, just real results.",
    },
    {
        name: "Arjun S.",
        location: "Beach Road · Sports injury",
        quote: "Tore my ACL playing cricket. Got a clear arthroscopic plan, rehab timeline and quick follow-ups. I'm back on the field this season — Dr. Prashanth's team is solid.",
    },
    {
        name: "Lakshmi K.",
        location: "MVP Colony · Gastro consult",
        quote: "Booked a gastro consult through the clinic — got a senior consultant who actually listened, ordered the right tests, and followed up. Felt like proper medicine, not a rushed visit.",
    },
    {
        name: "Anil K.",
        location: "Gajuwaka · Hair restoration",
        quote: "Came for hair fall and got a proper diagnosis, not just a generic shampoo. PRP sessions worked and the results are visible — finally a clinic that doesn't oversell.",
    },
];

const FAQS = [
    {
        q: "What specialities are available at Pranya Clinic?",
        a: "Pranya is a multi-specialty clinic offering orthopaedic care (with Dr. A. Prashanth, MS Orthopaedics), dermatology — medical, surgical & aesthetic (with Dr. Sai Sowjanya, MD DVL), and gastroenterology consultations with visiting senior consultants on scheduled days.",
    },
    {
        q: "Do I need an appointment to visit?",
        a: "Yes, we work by appointment to make sure you get unhurried, focused time with the right specialist. The easiest way to book is to WhatsApp us at +91 94910 72309, message us on Instagram, or call the clinic directly.",
    },
    {
        q: "What should I expect in my first consultation?",
        a: "A detailed history, focused examination (dermoscopy for skin, clinical and imaging review for ortho), discussion of options, and a written plan with realistic expectations, timelines, and costs upfront.",
    },
    {
        q: "What orthopaedic services do you offer?",
        a: "Dr. Prashanth handles joint pain, fractures, arthritis, sports injuries, arthroscopic (keyhole) surgery, knee, hip & elbow joint replacement, spine procedures, PRP, and hyaluronic acid injections for knee osteoarthritis — backed by 6+ years experience and 300+ surgeries.",
    },
    {
        q: "What dermatology and aesthetic services are available?",
        a: "Dr. Sowjanya offers medical dermatology (acne, eczema, psoriasis, pigmentation, hair loss, STIs), dermatosurgery (mole/cyst excision, ear lobe repair, biopsies), and aesthetic procedures including Botox, fillers, lip augmentation, non-surgical nose reshaping, PRP/GFC/QR678 hair therapy and laser treatments.",
    },
    {
        q: "When are the gastroenterology consultations available?",
        a: "Gastroenterology consultations run on scheduled days with visiting senior consultants. Please WhatsApp or call us to confirm the next available gastro slot before visiting.",
    },
    {
        q: "Do you offer video / online consultations?",
        a: "Yes — video consultations are available on request, especially helpful for follow-ups, second opinions, and patients outside Visakhapatnam. Please WhatsApp us at +91 94910 72309 to confirm specialist availability and schedule a slot.",
    },
    {
        q: "What languages do the doctors consult in?",
        a: "Consultations are offered in Telugu, English, and Hindi — whichever you are most comfortable in.",
    },
    {
        q: "What are the clinic hours?",
        a: "The clinic is open Monday to Sunday, 9:00 AM to 9:00 PM. Specific specialist availability varies by day, so please confirm your slot in advance.",
    },
];

/* --------------------------------- Header --------------------------------- */
const Header = () => {
    const [open, setOpen] = useState(false);
    return (
        <header
            data-testid="site-header"
            className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#1F5F6E]/10"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
                <a
                    href="#top"
                    data-testid="header-logo-link"
                    className="flex items-center gap-3"
                >
                    <img
                        src={LOGO_URL}
                        alt="Pranya Bone & Skin Clinic"
                        className="h-12 w-12 rounded-full object-cover ring-1 ring-[#1F5F6E]/10"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-lg font-semibold text-[#1F5F6E] tracking-tight">
                            Pranya
                        </div>
                        <div className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#E8A78A]">
                            Bone &amp; Skin Clinic
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <a
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            href={`#${l.id}`}
                            className="text-sm font-medium text-[#1A3036]/80 hover:text-[#1F5F6E] transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    <a
                        data-testid="header-call-link"
                        href={`tel:${PHONE_NUMBER}`}
                        className="text-sm font-medium text-[#1F5F6E] hover:text-[#154652] transition-colors flex items-center gap-2"
                    >
                        <Phone className="w-4 h-4" />
                        Call us
                    </a>
                    <a
                        data-testid="header-mail-link"
                        href={`mailto:${EMAIL}`}
                        className="text-sm font-medium text-[#1F5F6E] hover:text-[#154652] transition-colors flex items-center gap-2"
                    >
                        <Mail className="w-4 h-4" />
                        Mail us
                    </a>
                    <Button
                        asChild
                        data-testid="header-whatsapp-btn"
                        className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white px-5"
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <WhatsappIcon className="w-4 h-4 mr-2" />
                            WhatsApp
                        </a>
                    </Button>
                    <Button
                        asChild
                        data-testid="header-book-instagram-btn"
                        className="rounded-full bg-[#1F5F6E] hover:bg-[#154652] text-white px-5"
                    >
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Instagram className="w-4 h-4 mr-2" />
                            Instagram
                        </a>
                    </Button>
                </div>

                <button
                    data-testid="mobile-menu-toggle"
                    onClick={() => setOpen((s) => !s)}
                    aria-label="Toggle menu"
                    className="md:hidden p-2 rounded-full text-[#1F5F6E] hover:bg-[#F5E6E0] transition"
                >
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden border-t border-[#1F5F6E]/10 bg-white/95 backdrop-blur-xl"
                >
                    <div className="px-4 py-4 flex flex-col gap-3">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.id}
                                data-testid={`mobile-nav-${l.id}`}
                                href={`#${l.id}`}
                                onClick={() => setOpen(false)}
                                className="text-sm font-medium text-[#1A3036] py-2"
                            >
                                {l.label}
                            </a>
                        ))}
                        <Button
                            asChild
                            data-testid="mobile-whatsapp-btn"
                            className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white mt-2"
                        >
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <WhatsappIcon className="w-4 h-4 mr-2" />
                                Book on WhatsApp
                            </a>
                        </Button>
                        <Button
                            asChild
                            data-testid="mobile-book-instagram-btn"
                            className="rounded-full bg-[#1F5F6E] hover:bg-[#154652] text-white"
                        >
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <Instagram className="w-4 h-4 mr-2" />
                                Book on Instagram
                            </a>
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};

/* ---------------------------------- Hero ---------------------------------- */
const Hero = () => (
    <section
        id="top"
        data-testid="hero-section"
        className="relative overflow-hidden"
    >
        <div className="organic-blob w-[420px] h-[420px] -top-32 -left-24 bg-[#F5E6E0]" />
        <div className="organic-blob w-[360px] h-[360px] top-40 -right-16 bg-[#E8A78A]/40" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 lg:pt-24 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7 z-10">
                <div className="reveal">
                    <span className="overline">
                        Visakhapatnam · Bone &amp; Skin Specialists
                    </span>
                </div>
                <h1
                    data-testid="hero-title"
                    className="reveal reveal-delay-1 mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1F5F6E] leading-[1.05]"
                >
                    Complete care,{" "}
                    <span className="relative inline-block">
                        from bone to skin.
                        <svg
                            className="absolute -bottom-2 left-0 w-full"
                            height="10"
                            viewBox="0 0 200 10"
                            fill="none"
                            preserveAspectRatio="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M2 6 Q 50 0 100 5 T 198 4"
                                stroke="#E8A78A"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                </h1>
                <p
                    data-testid="hero-subtitle"
                    className="reveal reveal-delay-2 mt-7 text-lg text-[#4A6268] max-w-xl leading-relaxed"
                >
                    Pranya Bone &amp; Skin Clinic brings specialist
                    orthopaedic and dermatology care under one calm,
                    modern roof in Visakhapatnam — thoughtful diagnosis,
                    procedural skill, and treatment plans you can
                    actually follow. Pranya operates under the esteemed
                    umbrella of{" "}
                    <span className="font-semibold text-[#1A3036]">
                        Ayyappa Poly Clinics
                    </span>
                    , a trusted center for comprehensive healthcare.
                </p>
                <div className="reveal reveal-delay-3 mt-9 flex flex-wrap items-center gap-4">
                    <Button
                        asChild
                        data-testid="hero-whatsapp-btn"
                        className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white px-7 py-6 text-base"
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <WhatsappIcon className="w-5 h-5 mr-2" />
                            Book on WhatsApp
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </a>
                    </Button>
                    <Button
                        asChild
                        data-testid="hero-book-instagram-btn"
                        className="rounded-full bg-[#1F5F6E] hover:bg-[#154652] text-white px-7 py-6 text-base"
                    >
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Instagram className="w-5 h-5 mr-2" />
                            Book on Instagram
                        </a>
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        data-testid="hero-call-btn"
                        className="rounded-full border-2 border-[#1F5F6E] text-[#1F5F6E] hover:bg-[#1F5F6E] hover:text-white px-7 py-6 text-base"
                    >
                        <a href={`tel:${PHONE_NUMBER}`}>
                            <Phone className="w-4 h-4 mr-2" />
                            Call the Clinic
                        </a>
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        data-testid="hero-visit-btn"
                        className="rounded-full border-2 border-[#1F5F6E] text-[#1F5F6E] hover:bg-[#1F5F6E] hover:text-white px-7 py-6 text-base"
                    >
                        <a
                            href={MAPS_DIRECTIONS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <MapPin className="w-4 h-4 mr-2" />
                            Visit Us
                        </a>
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        data-testid="hero-mail-btn"
                        className="rounded-full border-2 border-[#1F5F6E] text-[#1F5F6E] hover:bg-[#1F5F6E] hover:text-white px-7 py-6 text-base"
                    >
                        <a href={`mailto:${EMAIL}`}>
                            <Mail className="w-4 h-4 mr-2" />
                            Mail Us
                        </a>
                    </Button>
                </div>

                <div className="reveal reveal-delay-4 mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-[#4A6268]">
                    <span className="inline-flex items-center gap-2">
                        <Bone className="w-4 h-4 text-[#1F5F6E]" /> MS
                        Orthopaedics &amp; MD Dermatology
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Award className="w-4 h-4 text-[#1F5F6E]" /> 300+
                        surgeries · 6+ years experience
                    </span>
                    <span className="inline-flex items-center gap-2">
                        <Languages className="w-4 h-4 text-[#1F5F6E]" /> Telugu ·
                        English · Hindi
                    </span>
                </div>
            </div>

            <div className="lg:col-span-5 z-10">
                <div className="relative reveal reveal-delay-2">
                    <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full bg-[#E8A78A]/30 blur-xl" />
                    <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full bg-[#1F5F6E]/10 blur-2xl" />
                    <div className="relative rounded-[2.5rem] overflow-hidden ring-1 ring-[#1F5F6E]/10 card-soft bg-white flex items-center justify-center p-8 sm:p-12 h-[460px] lg:h-[540px]">
                        <img
                            src={LOGO_URL}
                            alt="Pranya Bone & Skin Clinic logo"
                            className="w-full h-full object-contain"
                        />
                    </div>
                    <div
                        data-testid="hero-floating-badge"
                        className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl card-soft p-4 flex items-center gap-3 ring-1 ring-[#E5EBEA]"
                    >
                        <div className="flex -space-x-2">
                            <div className="w-10 h-10 rounded-full bg-[#1F5F6E] text-white flex items-center justify-center font-display font-semibold text-sm ring-2 ring-white">
                                AP
                            </div>
                            <div className="w-10 h-10 rounded-full bg-[#E8A78A] text-[#1A3036] flex items-center justify-center font-display font-semibold text-sm ring-2 ring-white">
                                SG
                            </div>
                            <div className="w-10 h-10 rounded-full bg-[#FCFBF8] text-[#1F5F6E] flex items-center justify-center font-display font-semibold text-sm ring-2 ring-white border border-[#1F5F6E]/15">
                                +
                            </div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-wider text-[#E8A78A] font-bold">
                                Multiple Specialities · One Clinic
                            </div>
                            <div className="text-sm font-semibold text-[#1A3036]">
                                Ortho · Derma · Gastro &amp; more
                            </div>
                            <div className="text-xs text-[#4A6268]">
                                Resident &amp; visiting consultants
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* --------------------------------- About ---------------------------------- */
const About = () => (
    <section
        id="about"
        data-testid="about-section"
        className="relative py-20 lg:py-28 bg-white"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
                <div className="rounded-[2rem] overflow-hidden ring-1 ring-[#1F5F6E]/10 card-soft">
                    <img
                        src="https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg"
                        alt="Inside Pranya Bone & Skin Clinic"
                        className="w-full h-[420px] object-cover"
                    />
                </div>
            </div>
            <div className="lg:col-span-7">
                <span className="overline">About the Clinic</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Multiple specialities. One calm clinic.
                </h2>
                <p className="mt-5 text-[#4A6268] text-base leading-relaxed">
                    Pranya was built on a simple belief — that great medical
                    care should feel unhurried, honest, and rooted in
                    evidence. We bring together specialties that rarely sit
                    under one roof:{" "}
                    <span className="font-semibold text-[#1A3036]">
                        orthopaedic care
                    </span>{" "}
                    for bones, joints, and movement,{" "}
                    <span className="font-semibold text-[#1A3036]">
                        dermatology
                    </span>{" "}
                    for skin, hair, and aesthetic well-being, and visiting{" "}
                    <span className="font-semibold text-[#1A3036]">
                        gastroenterology consultants
                    </span>{" "}
                    for digestive and gut health.
                </p>
                <p className="mt-4 text-[#4A6268] text-base leading-relaxed">
                    Located in the heart of Visakhapatnam, our clinic serves
                    patients across Andhra Pradesh — from joint pain,
                    fractures, and sports injuries on the orthopaedic side,
                    to acne, pigmentation, hair loss, vitiligo surgery, and
                    advanced aesthetic dermatology on the skin side, with
                    gastro consultations on scheduled days. Whatever you
                    walk in with, you&apos;ll leave with a plan you
                    understand.
                </p>

                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                    {[
                        { n: "3+", l: "Specialities under one roof" },
                        { n: "300+", l: "Surgeries performed" },
                        { n: "25+", l: "Treatment areas" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            data-testid={`about-stat-${i}`}
                            className="rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-5"
                        >
                            <div className="font-display text-3xl font-semibold text-[#1F5F6E]">
                                {s.n}
                            </div>
                            <div className="mt-1 text-sm text-[#4A6268]">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

/* --------------------------------- Doctor --------------------------------- */
const Doctor = () => (
    <section
        id="doctor-derm"
        data-testid="doctor-section"
        className="relative py-20 lg:py-28 bg-white"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-28">
                <div className="relative">
                    <div className="absolute -top-4 -right-4 w-28 h-28 rounded-full bg-[#E8A78A]/30 blur-2xl" />
                    <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#1F5F6E]/10 card-soft">
                        <img
                            src="https://customer-assets.emergentagent.com/job_pranya-skin/artifacts/izrdoqvp_WhatsApp%20Image%202026-06-07%20at%2016.27.54.jpeg"
                            alt="Dr. Sai Sowjanya Garimella, Dermatologist at Pranya Clinic"
                            className="w-full h-[520px] object-cover object-[center_20%]"
                        />
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md rounded-2xl ring-1 ring-[#E5EBEA] p-4">
                        <div className="overline">Meet your dermatologist</div>
                        <div className="mt-1 font-display text-xl font-semibold text-[#1F5F6E]">
                            Dr. Sai Sowjanya Garimella
                        </div>
                        <div className="text-sm text-[#4A6268]">
                            MBBS · MD (DVL)
                        </div>
                        <div className="mt-1 text-xs text-[#E8A78A] font-semibold tracking-wide uppercase">
                            Dermatologist · Cosmetologist · Dermatosurgeon
                        </div>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-7">
                <span className="overline">Dermatology Specialist</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Healthy skin.{" "}
                    <span className="text-[#E8A78A]">Confident you.</span>
                </h2>
                <div className="mt-3 text-sm font-medium text-[#1F5F6E]/80">
                    Consultant Dermatologist · Cosmetologist · Dermatosurgeon
                </div>

                <p className="mt-5 text-[#4A6268] leading-relaxed">
                    Dr. Sai Sowjanya Garimella is a dedicated dermatologist with
                    extensive training under renowned dermatologists in{" "}
                    <span className="font-semibold text-[#1A3036]">
                        Hyderabad
                    </span>
                    . Her clinical expertise spans the diagnosis and management
                    of a wide spectrum of skin, hair, nail, and sexually
                    transmitted conditions — combining evidence-based medicine
                    with a patient-centered approach.
                </p>
                <p className="mt-4 text-[#4A6268] leading-relaxed">
                    With comprehensive training in{" "}
                    <span className="font-semibold text-[#1A3036]">
                        medical, surgical, and aesthetic dermatology
                    </span>
                    , she is skilled in diagnosing and treating a wide range of
                    dermatological conditions while also performing advanced
                    cutaneous surgical procedures — from mole and cyst excisions
                    to ear lobe repair, wart removal, and skin biopsies.
                </p>

                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                        { n: "3+", l: "Years residency" },
                        { n: "MD", l: "Specialist (DVL)" },
                        { n: "Hyd.", l: "Trained dermatologist" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            data-testid={`derm-stat-${i}`}
                            className="rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-4 card-soft"
                        >
                            <div className="font-display text-2xl font-semibold text-[#1F5F6E]">
                                {s.n}
                            </div>
                            <div className="mt-1 text-xs text-[#4A6268] leading-snug">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    <div
                        data-testid="doctor-card-education"
                        className="rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-6 card-soft"
                    >
                        <GraduationCap className="w-5 h-5 text-[#E8A78A]" />
                        <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                            Qualifications &amp; Training
                        </div>
                        <ul className="mt-2 text-sm text-[#4A6268] space-y-1.5">
                            <li>
                                <span className="font-medium text-[#1A3036]">
                                    MD, Dermatology, Venereology &amp; Leprosy
                                </span>{" "}
                                — SVS Medical College, Mahbubnagar
                            </li>
                            <li>
                                <span className="font-medium text-[#1A3036]">
                                    MBBS
                                </span>{" "}
                                — Andhra Medical College, Visakhapatnam
                            </li>
                            <li>
                                Advanced training under renowned dermatologists
                                in Hyderabad
                            </li>
                        </ul>
                    </div>

                    <div
                        data-testid="doctor-card-philosophy"
                        className="rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-6 card-soft"
                    >
                        <Heart className="w-5 h-5 text-[#E8A78A]" />
                        <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                            Philosophy of Care
                        </div>
                        <p className="mt-2 text-sm text-[#4A6268] leading-relaxed">
                            Healthy skin is the foundation of confidence and
                            well-being. Every plan is personalised — combining
                            clinical excellence, surgical precision, and
                            artistic vision to help patients look and feel their
                            best.
                        </p>
                    </div>
                </div>

                {/* Areas of Expertise — grouped categories */}
                <div className="mt-8 rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-6 sm:p-7 card-soft">
                    <Sparkles className="w-5 h-5 text-[#E8A78A]" />
                    <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                        Areas of Expertise
                    </div>

                    <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-6">
                        {[
                            {
                                title: "Medical Dermatology",
                                items: [
                                    "Acne, eczema & psoriasis",
                                    "Pigmentation disorders",
                                    "Fungal infections & allergies",
                                    "Hair disorders & hair loss",
                                    "Nail disorders",
                                    "Sexually Transmitted Infections (STIs)",
                                    "Paediatric & adult dermatology",
                                ],
                            },
                            {
                                title: "Dermatosurgery",
                                items: [
                                    "Mole & cyst excisions",
                                    "Ear lobe repair",
                                    "Wart & skin tag removal",
                                    "Skin biopsies",
                                    "Minor dermatological procedures",
                                ],
                            },
                            {
                                title: "Aesthetic Dermatology",
                                items: [
                                    "Botox for wrinkles & facial rejuvenation",
                                    "Dermal fillers for facial contouring",
                                    "Lip augmentation",
                                    "Non-surgical nose reshaping",
                                    "Facial profiling & harmonisation",
                                    "Anti-ageing treatments",
                                    "Skin glow & rejuvenation",
                                ],
                            },
                            {
                                title: "Advanced Hair Restoration",
                                items: [
                                    "PRP (Platelet-Rich Plasma) Therapy",
                                    "GFC (Growth Factor Concentrate) Therapy",
                                    "QR678 Hair Regrowth Treatment",
                                    "Comprehensive hair loss evaluation",
                                ],
                            },
                            {
                                title: "Laser & Pigmentation Treatments",
                                items: [
                                    "Laser procedures for skin concerns",
                                    "Depigmentation treatments",
                                    "Scar & texture improvement",
                                    "Skin rejuvenation & resurfacing",
                                ],
                                wide: true,
                            },
                        ].map((group, idx) => (
                            <div
                                key={group.title}
                                data-testid={`derm-expertise-group-${idx}`}
                                className={group.wide ? "sm:col-span-2" : ""}
                            >
                                <div className="flex items-center gap-2">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E8A78A]" />
                                    <h4 className="font-display text-sm font-semibold text-[#1F5F6E] uppercase tracking-wider">
                                        {group.title}
                                    </h4>
                                </div>
                                <ul className="mt-3 space-y-1.5 text-sm text-[#4A6268]">
                                    {group.items.map((it) => (
                                        <li
                                            key={it}
                                            className="flex items-start gap-2"
                                        >
                                            <span className="text-[#E8A78A] mt-0.5">
                                                ·
                                            </span>
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ------------------------------- Doctor Ortho ----------------------------- */
const DOCTOR_ORTHO_IMG = "/images/dr-prashanth.png";

const DoctorOrtho = () => (
    <section
        id="doctors"
        data-testid="doctor-ortho-section"
        className="relative py-20 lg:py-28"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
            {/* Content on the LEFT, image on the RIGHT (visual alternation with Sowjanya section below) */}
            <div className="lg:col-span-7 lg:order-1 order-2">
                <span className="overline">Orthopaedic Specialist</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Restoring movement. Relieving pain.{" "}
                    <span className="text-[#E8A78A]">Rebuilding lives.</span>
                </h2>
                <div className="mt-3 text-sm font-medium text-[#1F5F6E]/80">
                    Consultant Orthopaedician · Assistant Professor, NRI
                    Institute of Medical Sciences (NRIIMS)
                </div>

                <p className="mt-5 text-[#4A6268] leading-relaxed">
                    Dr. A. Prashanth is a highly skilled orthopaedic surgeon
                    with over <span className="font-semibold text-[#1A3036]">6 years</span>{" "}
                    of dedicated experience in diagnosing and treating bone,
                    joint, muscle, and sports-related conditions. He currently
                    serves as an Assistant Professor at NRIIMS, where he is
                    actively involved in patient care, surgical training, and
                    academic excellence.
                </p>
                <p className="mt-4 text-[#4A6268] leading-relaxed">
                    Over his career, Dr. Prashanth has successfully performed
                    more than{" "}
                    <span className="font-semibold text-[#1A3036]">
                        300 orthopaedic surgeries
                    </span>{" "}
                    — helping patients regain mobility, reduce pain, and return
                    to active lifestyles. His approach blends evidence-based
                    medicine, advanced surgical techniques, and compassionate,
                    personalised care.
                </p>

                <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[
                        { n: "6+", l: "Years experience" },
                        { n: "300+", l: "Surgeries performed" },
                        { n: "NRIIMS", l: "Assistant Professor" },
                    ].map((s, i) => (
                        <div
                            key={i}
                            data-testid={`ortho-stat-${i}`}
                            className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-4 card-soft"
                        >
                            <div className="font-display text-2xl font-semibold text-[#1F5F6E]">
                                {s.n}
                            </div>
                            <div className="mt-1 text-xs text-[#4A6268] leading-snug">
                                {s.l}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                    <div
                        data-testid="doctor-ortho-card-qualifications"
                        className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-6 card-soft"
                    >
                        <GraduationCap className="w-5 h-5 text-[#E8A78A]" />
                        <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                            Qualifications &amp; Role
                        </div>
                        <ul className="mt-2 text-sm text-[#4A6268] space-y-1.5">
                            <li>
                                <span className="font-medium text-[#1A3036]">
                                    M.S. Orthopaedics
                                </span>
                            </li>
                            <li>
                                <span className="font-medium text-[#1A3036]">
                                    M.B.B.S.
                                </span>
                            </li>
                            <li>
                                Assistant Professor — NRI Institute of Medical
                                Sciences (NRIIMS)
                            </li>
                            <li>Consultant Orthopaedician — Pranya Bone &amp; Skin Clinic</li>
                        </ul>
                    </div>

                    <div
                        data-testid="doctor-ortho-card-approach"
                        className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-6 card-soft"
                    >
                        <Heart className="w-5 h-5 text-[#E8A78A]" />
                        <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                            Approach to Care
                        </div>
                        <ul className="mt-2 text-sm text-[#4A6268] space-y-1.5">
                            <li>Personalised treatment plans for every patient</li>
                            <li>Evidence-based, modern surgical techniques</li>
                            <li>Focus on faster recovery &amp; minimal downtime</li>
                            <li>Compassionate, patient-first communication</li>
                        </ul>
                    </div>

                    <div
                        data-testid="doctor-ortho-card-expertise"
                        className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-6 sm:p-7 card-soft sm:col-span-2"
                    >
                        <Sparkles className="w-5 h-5 text-[#E8A78A]" />
                        <div className="mt-3 font-display font-semibold text-[#1F5F6E]">
                            Areas of Expertise
                        </div>

                        <div className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-6">
                            {[
                                {
                                    title: "Joint Replacement & Reconstruction",
                                    items: [
                                        "Knee, hip & elbow joint replacement",
                                        "Arthroscopic (keyhole) surgery",
                                        "Arthroscopy — ACL, PCL, meniscus & shoulder (rotator cuff)",
                                        "Spine fixation & trauma",
                                        "Spine fixation & decompression (laminectomy & discectomy)",
                                    ],
                                },
                                {
                                    title: "Trauma & Sports Injury",
                                    items: [
                                        "Orthopaedic trauma & fractures",
                                        "Fracture fixation",
                                        "Managing bone infection",
                                        "Sports injury management",
                                    ],
                                },
                                {
                                    title: "Regenerative & Non-Surgical Care",
                                    items: [
                                        "PRP (Platelet-Rich Plasma) Therapy",
                                        "Hyaluronic acid injections (Knee OA)",
                                        "Musculoskeletal care",
                                    ],
                                    wide: true,
                                },
                            ].map((group, idx) => (
                                <div
                                    key={group.title}
                                    data-testid={`ortho-expertise-group-${idx}`}
                                    className={group.wide ? "sm:col-span-2" : ""}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E8A78A]" />
                                        <h4 className="font-display text-sm font-semibold text-[#1F5F6E] uppercase tracking-wider">
                                            {group.title}
                                        </h4>
                                    </div>
                                    <ul className="mt-3 space-y-1.5 text-sm text-[#4A6268]">
                                        {group.items.map((it) => (
                                            <li
                                                key={it}
                                                className="flex items-start gap-2"
                                            >
                                                <span className="text-[#E8A78A] mt-0.5">
                                                    ·
                                                </span>
                                                <span>{it}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-5 lg:order-2 order-1 lg:sticky lg:top-28">
                <div className="relative">
                    <div className="absolute -top-4 -left-4 w-28 h-28 rounded-full bg-[#1F5F6E]/15 blur-2xl" />
                    <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#1F5F6E]/10 card-soft">
                        <img
                            src={DOCTOR_ORTHO_IMG}
                            alt="Dr. A. Prashanth, Orthopaedic Surgeon at Pranya Clinic"
                            className="w-full h-[560px] object-cover"
                        />
                    </div>
                    <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md rounded-2xl ring-1 ring-[#E5EBEA] p-4">
                        <div className="overline">Meet your orthopaedic surgeon</div>
                        <div className="mt-1 font-display text-xl font-semibold text-[#1F5F6E]">
                            Dr. A. Prashanth
                        </div>
                        <div className="text-sm text-[#4A6268]">
                            M.B.B.S. · M.S. Orthopaedics
                        </div>
                        <div className="mt-1 text-xs text-[#E8A78A] font-semibold tracking-wide">
                            6+ YEARS · 300+ SURGERIES
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

/* ----------------------------- Gastro Consultants ------------------------- */
const GASTRO_CONSULTANTS = [
    {
        name: "Dr. N. Gowtham Kumar",
        photo: "https://customer-assets.emergentagent.com/job_pranya-skin/artifacts/yr6zjwmp_WhatsApp%20Image%202026-06-07%20at%2017.51.29.jpeg",
        credentials: [
            "MD General Medicine (Gold Medalist)",
            "DM – Medical Gastroenterology",
            "Advanced Therapeutic Endoscopist",
        ],
        registration: null,
    },
    {
        name: "Dr. Ravi Chandra Reddy Obili",
        photo: "https://customer-assets.emergentagent.com/job_pranya-skin/artifacts/cchfemaw_WhatsApp%20Image%202026-06-07%20at%2017.50.53.jpeg",
        credentials: [
            "MS General Surgery",
            "DrNB (Surgical Gastroenterology)",
            "Fellow in GI & HPB Onco Surgery (TMH)",
            "Fellow in Liver Transplant Surgery (FLT)",
        ],
        registration: null,
    },
];

const GastroConsultants = () => (
    <section
        id="gastro"
        data-testid="gastro-section"
        className="relative py-20 lg:py-24"
    >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
                <span className="overline">Visiting Consultants</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Gastroenterology consultations.
                </h2>
                <p className="mt-4 text-[#4A6268]">
                    Under the name of{" "}
                    <span className="font-medium text-[#1A3036]">
                        Ayyappa Poly Clinics
                    </span>
                    , we are supported by visiting specialists in Medical
                    Gastroenterology, Surgical Gastroenterology, and
                    Gynecology — enabling us to provide integrated,
                    patient-centered care under one roof. They visit Pranya
                    on scheduled days, so please WhatsApp or call to confirm
                    the next slot.
                </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 gap-5">
                {GASTRO_CONSULTANTS.map((c, i) => (
                    <div
                        key={c.name}
                        data-testid={`gastro-card-${i}`}
                        className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-6 card-soft card-soft-hover transition-all duration-300 flex gap-5 items-start"
                    >
                        <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden ring-2 ring-[#F5E6E0] bg-[#F5E6E0]">
                            <img
                                src={c.photo}
                                alt={c.name}
                                className="w-full h-full object-cover object-[50%_28%] scale-[2.3] origin-[50%_28%]"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="overline">Visiting Gastroenterologist</div>
                            <h3 className="mt-1 font-display text-lg font-semibold text-[#1F5F6E]">
                                {c.name}
                            </h3>
                            <ul className="mt-2 text-sm text-[#4A6268] space-y-1">
                                {c.credentials.map((cr) => (
                                    <li key={cr} className="flex items-start gap-2">
                                        <span className="text-[#E8A78A] mt-0.5">
                                            ·
                                        </span>
                                        <span>{cr}</span>
                                    </li>
                                ))}
                            </ul>
                            {c.registration ? (
                                <div className="mt-2 text-xs text-[#1F5F6E]/70 font-medium tracking-wide">
                                    {c.registration}
                                </div>
                            ) : null}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </section>
);

/* -------------------------------- Services -------------------------------- */
const Services = () => (
    <section
        id="services"
        data-testid="services-section"
        className="relative py-20 lg:py-28 bg-white"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
                <span className="overline">Treatments &amp; Services</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Complete care, from bone to skin.
                </h2>
                <p className="mt-4 text-[#4A6268]">
                    Multiple specialities, one calm clinic — orthopaedic care
                    led by Dr. A. Prashanth, dermatology led by Dr. Sai
                    Sowjanya Garimella, and visiting gastroenterology
                    consultants on scheduled days. Every plan is tailored to
                    your goals, lifestyle, and what you can realistically
                    maintain.
                </p>
            </div>

            {/* ---------- Orthopaedic Services ---------- */}
            <div className="mt-16 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <span className="overline">Orthopaedics</span>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl font-medium text-[#1F5F6E]">
                        Bone, joint &amp; sports injury care
                    </h3>
                    <p className="mt-1 text-sm text-[#4A6268]">
                        With Dr. A. Prashanth — M.B.B.S., M.S. Orthopaedics
                    </p>
                </div>
                <a
                    href="#doctors"
                    data-testid="services-meet-ortho"
                    className="text-sm font-medium text-[#1F5F6E] hover:text-[#E8A78A] inline-flex items-center gap-1"
                >
                    Meet Dr. Prashanth <ArrowRight className="w-4 h-4" />
                </a>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {SERVICES_ORTHO.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={s.title}
                            data-testid={`service-ortho-card-${i}`}
                            className="group rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-6 card-soft card-soft-hover hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="w-12 h-12 rounded-xl bg-[#1F5F6E]/10 flex items-center justify-center group-hover:bg-[#1F5F6E] transition-colors">
                                <Icon className="w-5 h-5 text-[#1F5F6E] group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-semibold text-[#1A3036]">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm text-[#4A6268] leading-relaxed">
                                {s.copy}
                            </p>
                        </div>
                    );
                })}
            </div>

            {/* Hairline divider */}
            <div className="mt-20 h-px bg-gradient-to-r from-transparent via-[#1F5F6E]/15 to-transparent" />

            {/* ---------- Dermatology Services ---------- */}
            <div className="mt-16 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <span className="overline">Dermatology</span>
                    <h3 className="mt-2 font-display text-2xl sm:text-3xl font-medium text-[#1F5F6E]">
                        Skin, hair &amp; aesthetic care
                    </h3>
                    <p className="mt-1 text-sm text-[#4A6268]">
                        With Dr. Sai Sowjanya Garimella — MBBS, MD (DVL)
                    </p>
                </div>
                <a
                    href="#doctor-derm"
                    data-testid="services-meet-derm"
                    className="text-sm font-medium text-[#1F5F6E] hover:text-[#E8A78A] inline-flex items-center gap-1"
                >
                    Meet Dr. Sowjanya <ArrowRight className="w-4 h-4" />
                </a>
            </div>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {SERVICES_DERM.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div
                            key={s.title}
                            data-testid={`service-derm-card-${i}`}
                            className="group rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-6 card-soft card-soft-hover hover:-translate-y-1 transition-all duration-300"
                        >
                            <div className="w-12 h-12 rounded-xl bg-[#F5E6E0] flex items-center justify-center group-hover:bg-[#E8A78A] transition-colors">
                                <Icon className="w-5 h-5 text-[#1F5F6E] group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-semibold text-[#1A3036]">
                                {s.title}
                            </h3>
                            <p className="mt-2 text-sm text-[#4A6268] leading-relaxed">
                                {s.copy}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
);

/* --------------------------------- Why Us --------------------------------- */
const WhyUs = () => (
    <section
        id="why-us"
        data-testid="why-us-section"
        className="relative py-20 lg:py-28"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
                <span className="overline">Why Patients Choose Pranya</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    One trusted clinic. Multiple specialities.
                </h2>
                <p className="mt-4 text-[#4A6268]">
                    Whether you&apos;re here for joints, skin, or gut health,
                    you&apos;ll experience the same calm care, clear plans,
                    and honest answers — every visit.
                </p>
            </div>

            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {WHY_US.map((w, i) => {
                    const Icon = w.icon;
                    return (
                        <div
                            key={w.title}
                            data-testid={`why-card-${i}`}
                            className="rounded-2xl bg-white ring-1 ring-[#E5EBEA] p-6 card-soft card-soft-hover transition-all duration-300 hover:-translate-y-1"
                        >
                            <div className="w-12 h-12 rounded-full bg-[#1F5F6E]/8 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-[#1F5F6E]" />
                            </div>
                            <h3 className="mt-5 font-display text-lg font-semibold text-[#1A3036]">
                                {w.title}
                            </h3>
                            <p className="mt-2 text-sm text-[#4A6268]">
                                {w.copy}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    </section>
);

/* ------------------------------ Testimonials ------------------------------ */
const Testimonials = () => (
    <section
        id="testimonials"
        data-testid="testimonials-section"
        className="relative py-20 lg:py-28 bg-white"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
                <span className="overline">In their words</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Patients across specialities.
                </h2>
                <p className="mt-4 text-[#4A6268]">
                    From knee replacements and sports injuries to skin glow and
                    gastro consults — real stories from real visits at Pranya.
                </p>
            </div>

            <div className="mt-12 grid md:grid-cols-3 gap-5">
                {TESTIMONIALS.map((t, i) => (
                    <figure
                        key={i}
                        data-testid={`testimonial-${i}`}
                        className="rounded-2xl bg-[#FCFBF8] ring-1 ring-[#E5EBEA] p-7 card-soft"
                    >
                        <Quote className="w-7 h-7 text-[#E8A78A]" />
                        <blockquote className="mt-4 text-[#1A3036] leading-relaxed">
                            &ldquo;{t.quote}&rdquo;
                        </blockquote>
                        <figcaption className="mt-5 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#1F5F6E] text-white flex items-center justify-center font-display font-semibold">
                                {t.name.charAt(0)}
                            </div>
                            <div>
                                <div className="text-sm font-semibold text-[#1A3036]">
                                    {t.name}
                                </div>
                                <div className="text-xs text-[#4A6268]">
                                    {t.location}
                                </div>
                            </div>
                        </figcaption>
                    </figure>
                ))}
            </div>
        </div>
    </section>
);

/* ----------------------------------- FAQ ---------------------------------- */
const FAQ = () => (
    <section id="faq" data-testid="faq-section" className="relative py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
                <span className="overline">Questions, answered</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    Before you book
                </h2>
            </div>

            <Accordion
                type="single"
                collapsible
                className="mt-12 rounded-2xl bg-white ring-1 ring-[#E5EBEA] card-soft divide-y divide-[#E5EBEA] overflow-hidden"
            >
                {FAQS.map((f, i) => (
                    <AccordionItem
                        key={i}
                        value={`faq-${i}`}
                        data-testid={`faq-item-${i}`}
                        className="px-6 border-0"
                    >
                        <AccordionTrigger className="text-left text-[#1A3036] font-display font-medium text-base hover:no-underline py-5">
                            {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-[#4A6268] leading-relaxed pb-5">
                            {f.a}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    </section>
);

/* --------------------------------- Contact -------------------------------- */
const Contact = () => (
    <section
        id="contact"
        data-testid="contact-section"
        className="relative py-20 lg:py-28 bg-white"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
                <span className="overline">Visit Pranya</span>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-[#1F5F6E]">
                    We&apos;d love to see you in the clinic.
                </h2>
                <p className="mt-4 text-[#4A6268]">
                    Appointments are easiest over WhatsApp or Instagram DMs,
                    or a quick phone call. Walk-ins are subject to
                    availability — please message us first.{" "}
                    <span className="font-medium text-[#1F5F6E]">
                        Video consultations are available on request.
                    </span>
                </p>

                <ul className="mt-8 space-y-5">
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <MapPin className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Address
                            </div>
                            <a
                                data-testid="contact-address"
                                href={MAPS_DIRECTIONS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#4A6268] hover:text-[#1F5F6E]"
                            >
                                {ADDRESS_LINE}
                                <span className="block text-xs text-[#1F5F6E] mt-0.5 underline-offset-2 hover:underline">
                                    Get directions →
                                </span>
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <Phone className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Phone
                            </div>
                            <a
                                data-testid="contact-phone"
                                href={`tel:${PHONE_NUMBER}`}
                                className="text-sm text-[#4A6268] hover:text-[#1F5F6E]"
                            >
                                {PHONE_DISPLAY}
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <WhatsappIcon className="w-4 h-4 text-[#25D366]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                WhatsApp
                            </div>
                            <a
                                data-testid="contact-whatsapp"
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#4A6268] hover:text-[#1F5F6E]"
                            >
                                Chat with us on WhatsApp
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <Mail className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Email
                            </div>
                            <a
                                data-testid="contact-email"
                                href={`mailto:${EMAIL}`}
                                className="text-sm text-[#4A6268] hover:text-[#1F5F6E]"
                            >
                                {EMAIL}
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <Instagram className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Instagram
                            </div>
                            <a
                                data-testid="contact-instagram"
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#4A6268] hover:text-[#1F5F6E]"
                            >
                                @pranya.boneskin_clinic
                            </a>
                        </div>
                    </li>
                    <li className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <Clock className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Clinic Hours
                            </div>
                            <div className="text-sm text-[#4A6268]">
                                Mon – Sun · 9:00 AM – 9:00 PM
                            </div>
                        </div>
                    </li>
                    <li
                        data-testid="contact-video-consult"
                        className="flex items-start gap-4"
                    >
                        <div className="w-10 h-10 rounded-full bg-[#F5E6E0] flex items-center justify-center shrink-0">
                            <Video className="w-4 h-4 text-[#1F5F6E]" />
                        </div>
                        <div>
                            <div className="font-display font-semibold text-[#1A3036]">
                                Video Consultations
                            </div>
                            <div className="text-sm text-[#4A6268]">
                                Available on request — WhatsApp or call to
                                schedule.
                            </div>
                        </div>
                    </li>
                </ul>

                <div className="mt-10 flex flex-wrap gap-3">
                    <Button
                        asChild
                        data-testid="contact-instagram-btn"
                        className="rounded-full bg-[#1F5F6E] hover:bg-[#154652] text-white px-7 py-6"
                    >
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Instagram className="w-5 h-5 mr-2" />
                            Message us on Instagram
                        </a>
                    </Button>
                    <Button
                        asChild
                        data-testid="contact-whatsapp-btn"
                        className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white px-7 py-6"
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <WhatsappIcon className="w-5 h-5 mr-2" />
                            Book on WhatsApp
                        </a>
                    </Button>
                    <Button
                        asChild
                        variant="outline"
                        data-testid="contact-call-btn"
                        className="rounded-full border-2 border-[#1F5F6E] text-[#1F5F6E] hover:bg-[#1F5F6E] hover:text-white px-7 py-6"
                    >
                        <a href={`tel:${PHONE_NUMBER}`}>
                            <Phone className="w-4 h-4 mr-2" />
                            Call Clinic
                        </a>
                    </Button>
                </div>
            </div>

            <div className="lg:col-span-7">
                <div className="rounded-[2rem] overflow-hidden ring-1 ring-[#1F5F6E]/10 card-soft h-full min-h-[480px]">
                    <iframe
                        data-testid="google-map-embed"
                        title="Pranya Bone & Skin Clinic Location"
                        src={MAPS_EMBED_URL}
                        className="w-full h-full min-h-[480px] border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                    />
                </div>
            </div>
        </div>
    </section>
);

/* ---------------------------------- Footer -------------------------------- */
const Footer = () => (
    <footer
        data-testid="site-footer"
        className="relative bg-[#1F5F6E] text-[#FCFBF8] pt-20 pb-10 mt-0"
    >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl bg-[#154652] ring-1 ring-white/10 p-8 sm:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div className="max-w-xl">
                    <span className="overline !text-[#E8A78A]">
                        Ready when you are
                    </span>
                    <h3 className="mt-3 font-display text-2xl sm:text-3xl font-semibold">
                        Book your consult on WhatsApp or Instagram.
                    </h3>
                    <p className="mt-3 text-white/75 text-sm">
                        Message us your concern and a few photos — our team
                        will help you choose the right consultation slot.
                    </p>
                </div>
                <div className="flex flex-wrap gap-3 shrink-0">
                    <Button
                        asChild
                        data-testid="footer-cta-whatsapp"
                        className="rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white font-semibold px-7 py-6 text-base"
                    >
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <WhatsappIcon className="w-5 h-5 mr-2" />
                            Book on WhatsApp
                        </a>
                    </Button>
                    <Button
                        asChild
                        data-testid="footer-cta-instagram"
                        className="rounded-full bg-[#E8A78A] hover:bg-[#D99A7F] text-[#1A3036] font-semibold px-7 py-6 text-base"
                    >
                        <a
                            href={INSTAGRAM_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <Instagram className="w-5 h-5 mr-2" />
                            Book on Instagram
                        </a>
                    </Button>
                </div>
            </div>

            <div className="mt-14 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5">
                    <div className="flex items-center gap-3">
                        <img
                            src={LOGO_URL}
                            alt="Pranya logo"
                            className="h-14 w-14 rounded-full ring-1 ring-white/20 object-cover"
                        />
                        <div>
                            <div className="font-display text-xl font-semibold">
                                Pranya Bone &amp; Skin Clinic
                            </div>
                            <div className="text-xs tracking-[0.18em] uppercase text-[#E8A78A]">
                                Visakhapatnam
                            </div>
                        </div>
                    </div>
                    <p className="mt-5 text-sm text-white/70 max-w-md">
                        A modern, evidence-based dermatology &amp; orthopaedic
                        practice — bringing skin, hair, and bone care under one
                        roof.
                    </p>
                </div>

                <div className="md:col-span-3">
                    <div className="text-sm font-semibold text-white">
                        Explore
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-white/70">
                        {NAV_LINKS.map((l) => (
                            <li key={l.id}>
                                <a
                                    href={`#${l.id}`}
                                    data-testid={`footer-link-${l.id}`}
                                    className="hover:text-white"
                                >
                                    {l.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <div className="text-sm font-semibold text-white">
                        Reach us
                    </div>
                    <ul className="mt-4 space-y-3 text-sm text-white/70">
                        <li className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-[#E8A78A] mt-0.5" />{" "}
                            {ADDRESS_LINE}
                        </li>
                        <li>
                            <a
                                href={`tel:${PHONE_NUMBER}`}
                                data-testid="footer-phone"
                                className="hover:text-white inline-flex items-center gap-2"
                            >
                                <Phone className="w-4 h-4 text-[#E8A78A]" />
                                {PHONE_DISPLAY}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="footer-email"
                                className="hover:text-white inline-flex items-center gap-2"
                            >
                                <Mail className="w-4 h-4 text-[#E8A78A]" />
                                {EMAIL}
                            </a>
                        </li>
                        <li>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-instagram"
                                className="hover:text-white inline-flex items-center gap-2"
                            >
                                <Instagram className="w-4 h-4 text-[#E8A78A]" />
                                @pranya.boneskin_clinic
                            </a>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/60">
                <div>
                    © {new Date().getFullYear()} Pranya Bone &amp; Skin Clinic.
                    All rights reserved.
                </div>
                <div className="flex items-center gap-2">
                    <Heart className="w-3 h-3 text-[#E8A78A]" />
                    Crafted with care in Visakhapatnam
                </div>
            </div>
        </div>
    </footer>
);

/* ---------------- Floating WhatsApp quick-action button ---------------- */
const FloatingWhatsapp = () => (
    <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-btn"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1FB855] text-white shadow-lg shadow-[#25D366]/30 px-4 py-3 transition-all hover:-translate-y-0.5"
    >
        <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-white/70 opacity-60 animate-ping" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-white" />
        </span>
        <WhatsappIcon className="w-5 h-5" />
        <span className="hidden sm:inline text-sm font-medium">
            Chat on WhatsApp
        </span>
    </a>
);

/* --------------------------------- Landing -------------------------------- */
export default function Landing() {
    useEffect(() => {
        document.title = "Pranya Bone & Skin Clinic | Visakhapatnam";
    }, []);

    return (
        <div data-testid="landing-page" className="bg-[#FCFBF8]">
            <Header />
            <main>
                <Hero />
                <About />
                <DoctorOrtho />
                <Doctor />
                <GastroConsultants />
                <Services />
                <WhyUs />
                <Testimonials />
                <FAQ />
                <Contact />
            </main>
            <Footer />
            <FloatingWhatsapp />
        </div>
    );
}
